import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, Calculator, Loader2 } from "lucide-react";
import {
  SHIPPING_MODES, LOCATION_TYPES, FEDEX_SERVICE_TYPES, CUSTOMER_TYPES, ACCESSORIALS,
} from "@/lib/constants";
import { useZipAutofill } from "@/lib/useZipAutofill";
import api from "@/lib/api";

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-caps">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Section({ title, children, cols = 2 }) {
  return (
    <div className="space-y-3">
      <div className="label-caps border-b border-border pb-1.5">{title}</div>
      <div className={`grid gap-3 ${cols === 2 ? "grid-cols-2" : "grid-cols-1"}`}>{children}</div>
    </div>
  );
}

function ProductCombobox({ products, value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = products.find((p) => p.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          data-testid="product-search-trigger"
          className="h-10 w-full justify-between font-normal"
        >
          <span className="truncate">{selected ? selected.name : "Search product…"}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search products…" data-testid="product-search-input" />
          <CommandList>
            <CommandEmpty>No product found.</CommandEmpty>
            <CommandGroup>
              {products.map((p) => (
                <CommandItem
                  key={p.id}
                  value={p.name}
                  data-testid={`product-option-${p.id}`}
                  onSelect={() => {
                    onChange(p.id);
                    setOpen(false);
                  }}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === p.id ? "opacity-100" : "opacity-0"}`} />
                  <div className="flex-1">
                    <div className="text-sm">{p.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {p.length}×{p.width}×{p.height}" · {p.weight}lb · {p.nesting_height > 0 ? "nestable" : "non-nest"}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function CustomerCombobox({ value, onSelectCustomer, onTypeCustomer }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const fetchList = (qry) => {
    setLoading(true);
    api.get(`/addresses${qry ? `?search=${encodeURIComponent(qry)}` : ""}`)
      .then((r) => setList((r.data || []).slice(0, 40)))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) fetchList(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSearch = (v) => {
    setSearch(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fetchList(v), 300);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          data-testid="qb-customer-trigger"
          className="h-10 w-full justify-between font-normal"
        >
          <span className="truncate">{value || "Select or type a customer…"}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search address book…" value={search} onValueChange={onSearch} data-testid="qb-customer-input" />
          <CommandList>
            {loading && <div className="py-4 text-center text-xs text-muted-foreground">Searching…</div>}
            {!loading && (
              <CommandEmpty>
                {search ? (
                  <button
                    type="button"
                    data-testid="qb-customer-use-typed-empty"
                    onClick={() => { onTypeCustomer(search); setOpen(false); }}
                    className="w-full px-2 py-1.5 text-left text-sm hover:text-[hsl(var(--primary))]"
                  >
                    Use “{search}” as customer
                  </button>
                ) : "Type to search the address book."}
              </CommandEmpty>
            )}
            {!loading && list.length > 0 && (
              <CommandGroup heading="Address Book">
                {list.map((a) => (
                  <CommandItem
                    key={a.id}
                    value={a.id}
                    data-testid={`qb-customer-option-${a.id}`}
                    onSelect={() => { onSelectCustomer(a); setOpen(false); }}
                  >
                    <Check className={`mr-2 h-4 w-4 ${value === a.company ? "opacity-100" : "opacity-0"}`} />
                    <div className="flex-1">
                      <div className="text-sm">{a.company}</div>
                      <div className="text-xs text-muted-foreground">
                        {[a.city, a.state, a.zip].filter(Boolean).join(", ")}{a.contact_name ? ` · ${a.contact_name}` : ""}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {!loading && search && (
              <CommandGroup>
                <CommandItem value={`__type__${search}`} onSelect={() => { onTypeCustomer(search); setOpen(false); }} data-testid="qb-customer-use-typed">
                  <span className="text-sm">Use “{search}” as a new customer</span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function QuoteBuilder({ form, setForm, products, onGetRates, loading, variant = "classic" }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAcc = (k, v) => setForm((f) => ({ ...f, accessorials: { ...f.accessorials, [k]: v } }));
  const zipFill = useZipAutofill(setForm);

  const [origins, setOrigins] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/ship-origins", { params: { active_only: true } })
      .then((r) => { if (alive) setOrigins(r.data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const selectShipFrom = (id) => {
    const o = origins.find((x) => x.id === id);
    if (!o) return;
    setForm((f) => ({
      ...f,
      ship_origin_id: o.id,
      origin_zip: o.zip || f.origin_zip,
      origin_city: o.city || f.origin_city,
      origin_state: o.state || f.origin_state,
      origin_company: o.company || o.label || f.origin_company,
      origin_address1: o.address1 || f.origin_address1,
      origin_contact_name: o.contact_name || f.origin_contact_name,
      origin_contact_phone: o.contact_phone || f.origin_contact_phone,
    }));
  };

  const selectCustomer = (a) =>
    setForm((f) => ({
      ...f,
      customer_name: a.company || "",
      customer_address_id: a.id || "",
      dest_company: a.company || "",
      destination_zip: a.zip || f.destination_zip,
      dest_city: a.city || f.dest_city,
      dest_state: a.state || f.dest_state,
      dest_address1: a.address1 || "",
      dest_contact_name: a.contact_name || "",
      dest_contact_phone: a.contact_phone || "",
      dest_contact_email: a.contact_email || "",
      delivery_location_type: a.location_type || f.delivery_location_type,
    }));
  const typeCustomer = (name) => setForm((f) => ({ ...f, customer_name: name, customer_address_id: "" }));

  return (
    <div className="border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="font-heading text-base font-bold tracking-tight">Quote Builder</h3>
      </div>
      <div className="space-y-6 p-4">
        <Section title="Customer & Reference">
          <Field label="Customer">
            <CustomerCombobox value={form.customer_name} onSelectCustomer={selectCustomer} onTypeCustomer={typeCustomer} />
          </Field>
          <Field label="Quote Reference">
            <Input value={form.quote_ref} onChange={(e) => set("quote_ref", e.target.value)} data-testid="qb-ref" className="h-10" />
          </Field>
          <Field label="Sales Rep">
            <Input value={form.sales_rep} onChange={(e) => set("sales_rep", e.target.value)} data-testid="qb-rep" className="h-10" />
          </Field>
          <Field label="Customer Type">
            <Select value={form.customer_type} onValueChange={(v) => set("customer_type", v)}>
              <SelectTrigger className="h-10" data-testid="qb-customer-type"><SelectValue /></SelectTrigger>
              <SelectContent>{CUSTOMER_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </Section>

        <Section title="Shipment">
          <div className="col-span-2">
            <Field label="Product">
              <ProductCombobox products={products} value={form.product_id} onChange={(v) => set("product_id", v)} />
            </Field>
          </div>
          <Field label="Quantity">
            <Input type="number" min={1} value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="qb-quantity" className="h-10" />
          </Field>
          <Field label="Shipping Mode">
            <Select value={form.shipping_mode} onValueChange={(v) => set("shipping_mode", v)}>
              <SelectTrigger className="h-10" data-testid="qb-mode"><SelectValue /></SelectTrigger>
              <SelectContent>{SHIPPING_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="FedEx Service">
            <Select value={form.fedex_service_type} onValueChange={(v) => set("fedex_service_type", v)}>
              <SelectTrigger className="h-10" data-testid="qb-fedex-service"><SelectValue /></SelectTrigger>
              <SelectContent>{FEDEX_SERVICE_TYPES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Declared Value ($)">
            <Input type="number" min={0} value={form.declared_value} onChange={(e) => set("declared_value", e.target.value)} data-testid="qb-declared" className="h-10" />
          </Field>
        </Section>

        <Section title="Origin & Destination">
          {origins.length > 0 && (
            <div className="col-span-2">
              <Field label="Ship From (PHS Location)">
                <Select value={form.ship_origin_id || ""} onValueChange={selectShipFrom}>
                  <SelectTrigger className="h-10" data-testid="qb-ship-origin">
                    <SelectValue placeholder="Select a PHS ship-from location (sets origin + FedEx account)" />
                  </SelectTrigger>
                  <SelectContent>
                    {origins.map((o) => (
                      <SelectItem key={o.id} value={o.id} data-testid={`qb-ship-origin-${o.id}`}>
                        {o.label}{o.city ? ` — ${o.city}, ${o.state} ${o.zip}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          )}
          <Field label="Origin ZIP">
            <Input value={form.origin_zip} onChange={(e) => zipFill(e.target.value, { zipKey: "origin_zip", cityKey: "origin_city", stateKey: "origin_state" })} data-testid="qb-origin-zip" className="h-10" placeholder="e.g. 60173" />
            {form.origin_city && <p className="mt-1 text-xs text-[hsl(var(--primary))]" data-testid="qb-origin-resolved">{form.origin_city}, {form.origin_state}</p>}
          </Field>
          <Field label="Destination ZIP">
            <Input value={form.destination_zip} onChange={(e) => zipFill(e.target.value, { zipKey: "destination_zip", cityKey: "dest_city", stateKey: "dest_state" })} data-testid="qb-dest-zip" className="h-10" placeholder="e.g. 30301" />
          </Field>
          <Field label="Dest City">
            <Input value={form.dest_city} onChange={(e) => set("dest_city", e.target.value)} data-testid="qb-dest-city" className="h-10" />
          </Field>
          <Field label="Dest State">
            <Input value={form.dest_state} onChange={(e) => set("dest_state", e.target.value)} data-testid="qb-dest-state" className="h-10" maxLength={2} />
          </Field>
          <Field label="Pickup Location">
            <Select value={form.pickup_location_type} onValueChange={(v) => set("pickup_location_type", v)}>
              <SelectTrigger className="h-10" data-testid="qb-pickup-loc"><SelectValue /></SelectTrigger>
              <SelectContent>{LOCATION_TYPES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Delivery Location">
            <Select value={form.delivery_location_type} onValueChange={(v) => set("delivery_location_type", v)}>
              <SelectTrigger className="h-10" data-testid="qb-delivery-loc"><SelectValue /></SelectTrigger>
              <SelectContent>{LOCATION_TYPES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Pickup Date">
            <Input type="date" value={form.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} data-testid="qb-pickup-date" className="h-10" />
          </Field>
        </Section>

        <Section title="Accessorials" cols={1}>
          {variant === "command" ? (
            <div className="flex flex-wrap gap-2">
              {ACCESSORIALS.map((a) => {
                const on = !!form.accessorials[a.key];
                return (
                  <button
                    key={a.key}
                    type="button"
                    onClick={() => setAcc(a.key, !on)}
                    data-testid={`acc-${a.key}`}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      on
                        ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))]"
                        : "border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground"
                    }`}
                  >
                    {a.label}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {ACCESSORIALS.map((a) => (
                <label key={a.key} className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                  <span className="text-sm">{a.label}</span>
                  <Switch
                    checked={!!form.accessorials[a.key]}
                    onCheckedChange={(v) => setAcc(a.key, v)}
                    data-testid={`acc-${a.key}`}
                  />
                </label>
              ))}
            </div>
          )}
        </Section>

        <Section title="Notes" cols={1}>
          <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="qb-notes" placeholder="Internal notes / special instructions…" />
        </Section>

        {variant !== "command" && (
          <Button
            onClick={onGetRates}
            disabled={loading || !form.product_id}
            data-testid="get-rates-button"
            className="h-11 w-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Calculator className="mr-2 h-4 w-4" /> Calculate & Compare Rates</>}
          </Button>
        )}
      </div>
      {variant === "command" && (
        <div className="sticky bottom-0 z-10 border-t border-border bg-card/90 p-4 backdrop-blur supports-[backdrop-filter]:bg-card/75">
          <Button
            onClick={onGetRates}
            disabled={loading || !form.product_id}
            data-testid="get-rates-button"
            className="h-11 w-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Calculator className="mr-2 h-4 w-4" /> Calculate & Compare Rates</>}
          </Button>
        </div>
      )}
    </div>
  );
}
